import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  HttpErrorResponse,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { WindowRefService } from '../window.service';
import { GlobalErrorService } from './global-error.service';
import { HelperService } from '../public/helper.service';
import { WhitelabelService } from '../shared/whitelabel.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private windowRefSer: WindowRefService,
    private storeErrorSer: GlobalErrorService,
    private helper: HelperService,
    private whitelabelService: WhitelabelService,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<any> {
    return next.handle(req).pipe(
      catchError((error) => {
        if (!error.errors) {
          error.errors = [];
        }
        if (!!error.error && !error.error.errors) {
          error.error.errors = [];
        }
        if (error instanceof HttpErrorResponse) {
          // Avoid redirect if 404
          const { path, method } = error.error ?? {};

          const avoidRedirect = [
            /^\/api\/v1\/stores\/.*\/orders\/.*/, // specific order
            /^\/api\/v1\/stores\/.*\/loyalty/, // loyalty membership
            /^\/api\/v1\/stores\/.*\/customers/,
            /^\/api\/v1\/auth\/.*/, // auth
            /^\/api\/v3\/integration\/stores\/.*\/IOTSOFT\/.*/,
            /^\/api\/v3\/integration\/stores\/.*\/WITEC\/.*/,
            /^\/api\/v1\/stores\/\d+$/, // store PATCH (e.g. change tenant)
            /^\/api\/v2\/stores\/.*\/orders\/.*/, // order submission & payment initiation
            /^\/api\/v1\/social\/facebook\/data-deletion\/.*/, // public data deletion status page
            /^\/api\/v1\/stores\/\d+\/catalog\/parent-offer\/\d+\/resolve/, // story #10478 shared item resolve — silent fallback on 404
            // XO floor plan lookups on the event editor. A floor plan is
            // optional on an event, so "no floor plan linked" (and a venue with
            // no floor plans at all) comes back as a 404 — an expected state,
            // not a page-level failure. Unanchored because these are matched
            // against the absolute XO url when the body carries no `path`.
            /\/v1\/venues\/\d+\/floorplans$/,
            /\/v1\/venues\/\d+\/events\/\d+\/floorplan$/,
          ];

          const isSecurityBlocked =
            error.status === 403 &&
            error.error?.errors?.some(
              (e) => e.code === 'SECURITY_REQUEST_BLOCKED',
            );

          if (
            isSecurityBlocked ||
            (error.status === 404 &&
              avoidRedirect.some((pattern) =>
                (path ?? req.url)?.match(pattern),
              ))
          ) {
            return throwError(error);
          }

          if (
            this.windowRefSer.nativeWindowLocation.hostname.startsWith(
              'admin',
            ) ||
            this.whitelabelService.isWhiteLabelDomainByHostName(
              this.windowRefSer.nativeWindowLocation.hostname,
            )
          ) {
            // Admin side
            const httpErrorStatus = (error as HttpErrorResponse).status;
            if (httpErrorStatus === 404 || httpErrorStatus === 500) {
              this.storeErrorSer.currentError = error;
              if (!this.helper.isMobileApp()) {
                this.router.navigate(['/manager/expectederror'], {
                  skipLocationChange: true,
                });
              }
            } else if (httpErrorStatus === 403) {
              this.storeErrorSer.isAccessDenied = true;
              if (!this.helper.isMobileApp()) {
                this.router.navigate(['/manager/expectederror'], {
                  skipLocationChange: true,
                });
              }
            }
            return throwError(error);
          }

          // TODO: check if 500 error handling is needed for public side
          switch ((error as HttpErrorResponse).status) {
            case 404:
            case 403:
              if (!this.helper.isMobileApp()) {
                this.router.navigate(['/not-found']);
              }
              return throwError(error);
            default:
              return throwError(error);
          }
        } else {
          return throwError(error);
        }
      }),
    );
  }
}
